/**
 * Modal Component — Size Guidelines
 *
 * | Size       | Use Case                                                    |
 * |------------|-------------------------------------------------------------|
 * | sm         | Alerts, confirmations, simple prompts, status messages      |
 * | md         | Standard forms, detail views, single-step actions           |
 * | lg         | Complex forms, previews, multi-section content              |
 * | xl         | Data tables, comparison views, detailed reports             |
 * | fullScreen | Rich text editors, dashboards, full-page workflows          |
 *
 * Mobile Behavior (mobileSheet prop, default: true):
 * - On screens below 640px (sm breakpoint), the modal renders as a bottom sheet
 *   that slides up from the bottom edge of the viewport.
 * - Set mobileSheet={false} to keep centered modal on all screen sizes.
 *
 * Nested Modal Rules:
 * - Maximum 3 levels of nested modals are allowed.
 * - Each nested level receives an incremented z-index and darker backdrop.
 * - Use ModalStackProvider at the layout level to enable stack tracking.
 */

'use client';

import { useEffect, useRef, useCallback, createContext, useContext, useMemo, useState } from 'react';
import { cn } from '@/core/utils/cn';
import { X } from 'lucide-react';
import { useIsMobile } from '@/core/hooks/useMediaQuery';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Modal Stack Context (P3-35: Nested modal detection)                       */
/* -------------------------------------------------------------------------- */

interface ModalStackContextValue {
  depth: number;
  push: () => number;
  pop: () => void;
  canOpen: boolean;
}

const ModalStackContext = createContext<ModalStackContextValue>({
  depth: 0,
  push: () => 0,
  pop: () => {},
  canOpen: true,
});

const MAX_MODAL_DEPTH = 4;

export function ModalStackProvider({ children }: { children: React.ReactNode }) {
  const [depth, setDepth] = useState(0);

  const push = useCallback(() => {
    let newDepth = 0;
    setDepth((prev) => {
      newDepth = prev + 1;
      return newDepth;
    });
    return newDepth;
  }, []);

  const pop = useCallback(() => {
    setDepth((prev) => Math.max(0, prev - 1));
  }, []);

  const canOpen = depth < MAX_MODAL_DEPTH;

  const value = useMemo(
    () => ({ depth, push, pop, canOpen }),
    [depth, push, pop, canOpen]
  );

  return (
    <ModalStackContext.Provider value={value}>
      {children}
    </ModalStackContext.Provider>
  );
}

export function useModalStack() {
  return useContext(ModalStackContext);
}

/* -------------------------------------------------------------------------- */
/*  Modal Component                                                           */
/* -------------------------------------------------------------------------- */

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, modal fills the entire viewport */
  fullScreen?: boolean;
  className?: string;
  id?: string;
  /** When true (default), renders as bottom sheet on mobile viewports */
  mobileSheet?: boolean;
}

const sizeStyles = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  size = 'md',
  fullScreen = false,
  className,
  id,
  mobileSheet = true,
}: ModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const [modalDepth, setModalDepth] = useState(0);
  const isMobile = useIsMobile();
  const { push, pop, canOpen } = useModalStack();

  const isBottomSheet = mobileSheet && isMobile && !fullScreen;
  const { t } = useTranslation();

  useEffect(() => {
    if (open) {
      if (!canOpen) return;
      const depth = push();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync local UI state from modal stack on open
      setModalDepth(depth);
      previousFocusRef.current = document.activeElement as HTMLElement;
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => {
        const firstFocusable = dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        firstFocusable?.focus();
      });
    } else {
      pop();
      document.body.style.overflow = '';
      previousFocusRef.current?.focus();
    }
    return () => { document.body.style.overflow = ''; };
  }, [open, push, pop, canOpen]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key === 'Tab' && dialogRef.current) {
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, [onClose]);

  if (!open) return null;
  if (!canOpen && modalDepth === 0) return null;

  const currentDepth = modalDepth;
  const zIndex = 50 + (currentDepth * 10);
  const backdropOpacity = Math.min(0.6 + (currentDepth * 0.1), 0.85);

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
      className={cn(
        'fixed inset-0 backdrop-blur-sm',
        isBottomSheet
          ? 'flex items-end justify-center'
          : fullScreen
            ? 'flex items-stretch justify-stretch p-0'
            : 'flex items-center justify-center p-4',
      )}
      style={{
        zIndex,
        backgroundColor: `rgba(0, 0, 0, ${backdropOpacity})`,
      }}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={dialogRef}
        id={id}
        className={cn(
          'bg-hms-card shadow-2xl',
          fullScreen
            ? 'w-screen h-screen flex flex-col overflow-hidden'
            : isBottomSheet
              ? 'w-full max-w-[100vw] border border-hms-border rounded-t-2xl max-h-[85vh] overflow-y-auto'
              : cn('w-full max-w-[calc(100vw-2rem)] border border-hms-border rounded-2xl', sizeStyles[size]),
          className
        )}
      >
        {isBottomSheet && (
          <div className="flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 rounded-full bg-hms-border" />
          </div>
        )}
        {(title || description) && (
          <div className="flex items-start justify-between p-4 sm:p-6 pb-0">
            <div>
              {title && <h2 className="text-[16px] font-semibold text-hms-text">{title}</h2>}
              {description && <p className="text-[12px] text-hms-textDim mt-1">{description}</p>}
            </div>
            <button id="shared-modal-close"
              onClick={onClose}
              aria-label={t('shared.modal.closeDialog')}
              className="text-hms-textDim hover:text-hms-text transition-colors p-1 -mt-1 -mr-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        <div className={cn('p-4 sm:p-6', fullScreen && 'flex-1 overflow-y-auto')}>{children}</div>
      </div>
    </div>
  );
}
